<script>
  import { createEventDispatcher } from 'svelte';
  import { clientStore } from '../../stores/client';
  import { projectStore } from '../../stores/project';
  import { workOrderStore } from '../../stores/work-order';

  const dispatch = createEventDispatcher();

  $: clients = $clientStore.items || [];
  $: projects = $projectStore.items || [];
  $: workOrders = $workOrderStore.items || [];
  $: loading = $clientStore.loading;

  function handleNewProject(clientId) {
    dispatch('newProject', { clientId });
  }

  function handleSendUpdate(clientId) {
    dispatch('sendUpdate', { clientId });
  }

  let search = '';
  let expanded = new Set();

  $: filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s);
  });

  function toggleExpand(id) {
    if (expanded.has(id)) expanded.delete(id);
    else expanded.add(id);
    expanded = expanded;
  }

  function getClientProjects(clientId) {
    return projects.filter(p => p.client === clientId);
  }

  function getClientWorkOrders(clientId) {
    const clientProjectIds = getClientProjects(clientId).map(p => p.id);
    return workOrders.filter(wo => clientProjectIds.includes(wo.project));
  }

  function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  }

  function statusConfig(status) {
    if (status === 'COMPLETED') return { label: 'Completed', class: 'status-success' };
    if (status === 'IN_PROGRESS') return { label: 'Active', class: 'status-active' };
    if (status === 'PLANNING') return { label: 'Planning', class: 'status-warning' };
    return { label: status || 'Unknown', class: 'status-default' };
  }

  $: totalActiveProjects = clients.reduce((sum, c) => {
    return sum + getClientProjects(c.id).filter(p => p.status === 'IN_PROGRESS').length;
  }, 0);

  $: totalValue = clients.reduce((sum, c) => {
    return sum + getClientProjects(c.id).reduce((pSum, p) => pSum + (p.budget || 0), 0);
  }, 0);
</script>

<div class="client-hub">
  <!-- Command Bar -->
  <div class="command-bar">
    <div class="search-box">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="text" placeholder="Search clients..." bind:value={search} />
    </div>
    <span class="count">{filtered.length} clients</span>
  </div>

  <!-- Quick Stats -->
  <div class="stats-row">
    <div class="stat">
      <span class="stat-value">{filtered.length}</span>
      <span class="stat-label">Clients</span>
    </div>
    <div class="stat">
      <span class="stat-value">{totalActiveProjects}</span>
      <span class="stat-label">Active Projects</span>
    </div>
    <div class="stat">
      <span class="stat-value">{formatCurrency(totalValue)}</span>
      <span class="stat-label">Total Value</span>
    </div>
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
    </div>
  {:else if filtered.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      <p>No clients found</p>
    </div>
  {:else}
    <div class="client-grid">
      {#each filtered as client (client.id)}
        {@const clientProjects = getClientProjects(client.id)}
        {@const clientWOs = getClientWorkOrders(client.id)}
        {@const isExpanded = expanded.has(client.id)}
        {@const activeProjects = clientProjects.filter(p => p.status === 'IN_PROGRESS')}
        {@const pendingWOs = clientWOs.filter(wo => wo.status !== 'COMPLETED')}
        {@const totalBudget = clientProjects.reduce((sum, p) => sum + (p.budget || 0), 0)}

        <div class="client-card" class:expanded={isExpanded}>
          <!-- Card Header -->
          <div class="card-header" on:click={() => toggleExpand(client.id)} on:keydown={() => {}} role="button" tabindex="0">
            <div class="header-main">
              <div class="client-title">
                <div class="avatar">{client.name?.charAt(0).toUpperCase() || 'C'}</div>
                <div class="title-text">
                  <h3>{client.name}</h3>
                  <div class="quick-stats">
                    {#if activeProjects.length > 0}
                      <span class="stat-tag active">{activeProjects.length} active</span>
                    {/if}
                    {#if pendingWOs.length > 0}
                      <span class="stat-tag pending">{pendingWOs.length} tasks pending</span>
                    {/if}
                    {#if clientProjects.length === 0}
                      <span class="stat-tag empty">No projects</span>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
            <div class="header-right">
              {#if totalBudget > 0}
                <span class="budget-tag">{formatCurrency(totalBudget)}</span>
              {/if}
              <svg class="expand-icon" class:rotated={isExpanded} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <!-- Expandable Content -->
          {#if isExpanded}
            <div class="card-content">
              <!-- Active Projects -->
              {#if clientProjects.length > 0}
                <div class="section">
                  <div class="section-header">
                    <h4>Projects</h4>
                    <span class="section-count">{clientProjects.length}</span>
                  </div>
                  <div class="project-list">
                    {#each clientProjects as project}
                      {@const status = statusConfig(project.status)}
                      {@const projectWOs = clientWOs.filter(wo => wo.project === project.id)}
                      {@const activeWOs = projectWOs.filter(wo => wo.status !== 'COMPLETED').length}
                      <div class="project-item">
                        <div class="project-main">
                          <span class="project-name">{project.name}</span>
                          <div class="project-meta">
                            <span class="status-dot {status.class}"></span>
                            <span>{status.label}</span>
                            {#if activeWOs > 0}
                              <span class="task-count">{activeWOs} task{activeWOs !== 1 ? 's' : ''}</span>
                            {/if}
                          </div>
                        </div>
                        <div class="project-progress">
                          <div class="progress-bar">
                            <div class="progress-fill" style="width: {project.progress || 0}%"></div>
                          </div>
                          <span>{project.progress || 0}%</span>
                        </div>
                      </div>
                    {/each}
                  </div>
                </div>
              {:else}
                <div class="empty-section">No projects yet - ready to start something new?</div>
              {/if}

              <!-- Contact Info (collapsed) -->
              <details class="details-section">
                <summary class="clickable-summary">
                  <div class="summary-left">
                    <span>Contact Information</span>
                    <span class="tap-hint">tap to expand</span>
                  </div>
                  <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div class="details-content">
                  <div class="detail-row">
                    <span class="detail-label">Email</span>
                    <a href="mailto:{client.email}" class="detail-value link">{client.email || '-'}</a>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Phone</span>
                    <a href="tel:{client.phone}" class="detail-value link">{client.phone || '-'}</a>
                  </div>
                  {#if client.address}
                    <div class="detail-row">
                      <span class="detail-label">Address</span>
                      <span class="detail-value">{client.address}</span>
                    </div>
                  {/if}
                </div>
              </details>

              <!-- Quick Actions -->
              <div class="actions">
                <button class="action-btn primary" on:click|stopPropagation={() => handleNewProject(client.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 4v16m-8-8h16" />
                  </svg>
                  New Project
                </button>
                <button class="action-btn" on:click|stopPropagation={() => handleSendUpdate(client.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Update
                </button>
              </div>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .client-hub {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Command Bar */
  .command-bar {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  .search-box {
    flex: 1;
    min-width: 200px;
    max-width: 320px;
    position: relative;
  }

  .search-box svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--text-muted);
  }

  .search-box input {
    padding-left: 38px;
    height: 36px;
  }

  .count {
    font-size: 13px;
    color: var(--text-muted);
  }

  /* Stats Row */
  .stats-row {
    display: flex;
    gap: 24px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  /* Loading & Empty States */
  .loading-state, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    color: var(--text-muted);
  }

  .empty-state svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  /* Client Grid */
  .client-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Client Card */
  .client-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    transition: border-color 150ms, box-shadow 150ms;
  }

  .client-card:hover {
    border-color: #cbd5e1;
  }

  .client-card.expanded {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }

  /* Card Header */
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
    gap: 12px;
  }

  .header-main {
    flex: 1;
    min-width: 0;
  }

  .client-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .avatar {
    width: 36px;
    height: 36px;
    background: var(--primary);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .title-text h3 {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    margin: 0;
  }

  .quick-stats {
    display: flex;
    gap: 6px;
    margin-top: 4px;
    flex-wrap: wrap;
  }

  .stat-tag {
    font-size: 10px;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .stat-tag.active {
    background: #dbeafe;
    color: #1e40af;
  }

  .stat-tag.pending {
    background: #fef3c7;
    color: #92400e;
  }

  .stat-tag.empty {
    background: var(--bg);
    color: var(--text-muted);
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .budget-tag {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  .expand-icon {
    width: 16px;
    height: 16px;
    color: var(--text-muted);
    transition: transform 150ms;
  }

  .expand-icon.rotated {
    transform: rotate(180deg);
  }

  /* Card Content */
  .card-content {
    padding: 0 16px 16px;
    border-top: 1px solid var(--border);
  }

  /* Section */
  .section {
    padding: 14px 0;
    border-bottom: 1px solid var(--border);
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .section-header h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin: 0;
  }

  .section-count {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    background: var(--bg);
    padding: 2px 6px;
    border-radius: 4px;
  }

  /* Project List */
  .project-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .project-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: var(--bg);
    border-radius: 4px;
    gap: 12px;
  }

  .project-main {
    flex: 1;
    min-width: 0;
  }

  .project-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    display: block;
  }

  .project-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 11px;
    color: var(--text-muted);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .status-dot.status-success { background: #22c55e; }
  .status-dot.status-active { background: #3b82f6; }
  .status-dot.status-warning { background: #f59e0b; }
  .status-dot.status-default { background: #94a3b8; }

  .task-count {
    color: var(--accent);
    font-weight: 500;
  }

  .project-progress {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .progress-bar {
    width: 60px;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--accent);
    border-radius: 2px;
    transition: width 200ms;
  }

  .project-progress span {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-muted);
    min-width: 32px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .empty-section {
    font-size: 12px;
    color: var(--text-muted);
    padding: 16px 0;
    text-align: center;
  }

  /* Details Section */
  .details-section {
    padding: 10px 0;
  }

  .details-section summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    cursor: pointer;
    padding: 8px 10px;
    list-style: none;
    background: var(--bg);
    border-radius: 6px;
    border: 1px solid var(--border);
    transition: all 150ms;
  }

  .details-section summary:hover {
    background: #fff;
    border-color: var(--accent);
    color: var(--text);
  }

  .details-section summary::-webkit-details-marker {
    display: none;
  }

  .summary-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tap-hint {
    font-size: 9px;
    font-weight: 400;
    color: var(--text-muted);
    opacity: 0.7;
    text-transform: lowercase;
  }

  .details-section summary:hover .tap-hint {
    opacity: 0;
  }

  .details-section summary .chevron {
    width: 14px;
    height: 14px;
    transition: transform 150ms;
  }

  .details-section[open] summary {
    border-color: var(--accent);
    background: #fff;
  }

  .details-section[open] summary .tap-hint {
    display: none;
  }

  .details-section[open] summary .chevron {
    transform: rotate(180deg);
  }

  .details-content {
    padding-top: 10px;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .detail-label {
    font-size: 11px;
    color: var(--text-muted);
  }

  .detail-value {
    font-size: 12px;
    color: var(--text);
  }

  .detail-value.link {
    text-decoration: none;
  }

  .detail-value.link:hover {
    color: var(--accent);
  }

  /* Actions */
  .actions {
    display: flex;
    gap: 8px;
    padding-top: 14px;
  }

  .action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #fff;
    color: var(--text);
    cursor: pointer;
    transition: all 100ms;
  }

  .action-btn:hover {
    background: var(--bg);
  }

  .action-btn.primary {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
  }

  .action-btn.primary:hover {
    background: var(--primary-hover);
  }

  .action-btn svg {
    width: 14px;
    height: 14px;
  }

  /* Mobile Responsive */
  @media (max-width: 767px) {
    .command-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .search-box {
      max-width: none;
    }

    .stats-row {
      justify-content: space-between;
      gap: 12px;
    }

    .stat-value {
      font-size: 16px;
    }

    .project-item {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }

    .project-progress {
      justify-content: space-between;
    }

    .progress-bar {
      flex: 1;
    }

    .actions {
      flex-direction: column;
    }
  }
</style>
